import isEqual from "lodash/isEqual";
import { Customer } from "../../customers/Customer";
import {
  EnrichStatusFilter,
  PageSizeStatus,
  ParentChildStatus,
  ProductStatusFilter,
  TextStatus,
  TranslationStatus,
} from "./ProductListTypes";
import { ChannelLanguagePair, ProductListParams } from "./ProductListURLConfig";
import { ColumnValue } from "./columns/Columns";

/**
 * This function is responsible for creating a ProductListParams object with default values set.
 * @param customer current customer
 * @param params Optional: pass this to populate keys in the default filter to create the compare object with other values than the defaults
 * @returns Partial<ProductListParams>
 */
export const createCompareFilter = (
  customer: Customer,
  params?: Partial<ProductListParams>
): Partial<ProductListParams> => {
  const compare: Partial<ProductListParams> = {
    brands: undefined,
    columns: [] as ColumnValue[],
    date_category: "created",
    date_end: undefined,
    date_start: undefined,
    label: undefined,
    channel_language_pairs: [
      {
        channel_id: customer?.data.default_channel,
        language_code: customer?.config?.tag_input_language || "en_US",
      },
    ] as ChannelLanguagePair[],
    pagination_size: parseInt(PageSizeStatus.Five),
    parent_child: ParentChildStatus.All,
    status: ProductStatusFilter.All,
    template: undefined,
    text_status: TextStatus.All,
    translation_status: TranslationStatus.All,
    enrich_status: EnrichStatusFilter.All,
    query: undefined,
    vocabulary: undefined,
    search: "",
  };
  if (params) Object.assign(compare, params);
  return compare;
};

/**
 * This function compares two ProductListParams objects and see if they differ in anyway.
 * It is using function `createCompareFilter` to create a default ProductListParams object to compare against
 * @param customer current customer
 * @param currentParameters current ProductListParams
 * @param compareParameters Optional: Use this if you want to compare against other saved filters (Like a active view)
 * @returns boolean
 */
export const hasActiveFilters = (
  customer: Customer,
  currentParameters: ProductListParams,
  compareParameters?: Partial<ProductListParams>
): boolean => {
  const compareFilter = createCompareFilter(customer, compareParameters);
  const currentFilter: {
    [key: string]: unknown;
  } = {};
  Object.entries(compareFilter).forEach(([key, value]) => {
    const valueToUse =
      currentParameters[key as keyof ProductListParams] || value;

    currentFilter[key] = valueToUse;
  });
  return !isEqual(compareFilter, currentFilter);
};

/** Filter keys that mean the saved view was edited (ignore view/page/columns noise). */
const VIEW_FILTER_KEYS: (keyof ProductListParams)[] = [
  "brands",
  "date_category",
  "date_end",
  "date_start",
  "label",
  "channel_language_pairs",
  "pagination_size",
  "parent_child",
  "status",
  "template",
  "text_status",
  "translation_status",
  "enrich_status",
  "query",
  "vocabulary",
  "search",
];

const isEmptyFilterValue = (value: unknown): boolean =>
  value === undefined ||
  value === null ||
  value === "" ||
  (Array.isArray(value) && value.length === 0);

/** Saved views store pairs as channel/language keys; the URL uses channel_language_pairs. */
const channelLanguagePairsFromParams = (
  params: Partial<ProductListParams> & Record<string, unknown>
): { channel_id: number; language_code: string }[] | undefined => {
  if (
    Array.isArray(params.channel_language_pairs) &&
    params.channel_language_pairs.length > 0
  ) {
    return params.channel_language_pairs.map((pair) => ({
      channel_id: pair.channel_id,
      language_code: pair.language_code,
    }));
  }
  const pairs: { channel_id: number; language_code: string }[] = [];
  const channel = params.channel;
  const language = params.language;
  if (channel != null && language) {
    const channelId = parseInt(String(channel), 10);
    if (!isNaN(channelId)) {
      pairs.push({
        channel_id: channelId,
        language_code: String(language),
      });
    }
  }
  for (let i = 2; ; i++) {
    const channelValue = params[`channel_${i}`];
    const languageValue = params[`language_${i}`];
    if (channelValue === undefined || languageValue === undefined) break;
    const channelId = parseInt(String(channelValue), 10);
    if (isNaN(channelId)) break;
    pairs.push({
      channel_id: channelId,
      language_code: String(languageValue),
    });
  }
  return pairs.length > 0 ? pairs : undefined;
};

/**
 * True when current list filters differ from a saved view's filters.
 * Ignores non-filter params (view, page, columns, …) that often differ
 * without the user having edited the filter.
 */
export const haveViewFiltersChanged = (
  customer: Customer,
  currentParameters: ProductListParams,
  viewParameters: Partial<ProductListParams>
): boolean => {
  const defaults = createCompareFilter(customer);
  const normalize = (
    params: Partial<ProductListParams> & Record<string, unknown>
  ): Record<string, unknown> => {
    const normalized: Record<string, unknown> = {};
    const withPairs = {
      ...params,
      channel_language_pairs:
        channelLanguagePairsFromParams(params) ?? params.channel_language_pairs,
    };
    VIEW_FILTER_KEYS.forEach((key) => {
      let value = withPairs[key];
      if (key === "pagination_size" && value != null && value !== "") {
        value = parseInt(String(value), 10);
      }
      normalized[key] = isEmptyFilterValue(value) ? defaults[key] : value;
    });
    return normalized;
  };
  return !isEqual(
    normalize(currentParameters),
    normalize(
      viewParameters as Partial<ProductListParams> & Record<string, unknown>
    )
  );
};
